'use client';

import { useCallback, useSyncExternalStore } from 'react';

function subscribeInstalled(onStoreChange: () => void) {
  const mq = window.matchMedia('(display-mode: standalone)');
  mq.addEventListener('change', onStoreChange);
  return () => mq.removeEventListener('change', onStoreChange);
}

function getInstalledSnapshot() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari standalone
    ('standalone' in navigator &&
      (navigator as Navigator & { standalone?: boolean }).standalone === true)
  );
}

function getInstalledServerSnapshot() {
  return false;
}

/**
 * Haptic feedback for standalone PWA. Uses `navigator.vibrate` with sensible
 * patterns; no-op on desktop or when vibration is unavailable.
 */
export function useHaptics() {
  const installed = useSyncExternalStore(
    subscribeInstalled,
    getInstalledSnapshot,
    getInstalledServerSnapshot
  );

  const vibrate = useCallback(
    (pattern: number | number[]) => {
      if (!installed || typeof navigator === 'undefined') return;
      navigator.vibrate?.(pattern);
    },
    [installed]
  );

  return {
    selection: useCallback(() => vibrate(10), [vibrate]),
    success: useCallback(() => vibrate([10, 50, 10]), [vibrate]),
    warning: useCallback(() => vibrate([20, 40, 20]), [vibrate]),
    error: useCallback(() => vibrate([30, 20, 30, 20, 30]), [vibrate]),
    impact: useCallback(
      (style: 'light' | 'medium' | 'heavy' = 'light') => {
        const ms = style === 'light' ? 10 : style === 'medium' ? 20 : 30;
        vibrate(ms);
      },
      [vibrate]
    )
  };
}
